import { defineStore } from 'pinia'
import catalogApi from '@/api/catalog'
import { createLatestRequestGate, entityName } from '@/features/catalog/shared/catalog.mjs'

const emptyList = () => ({ items: [], count: 0 })
const characteristicsGate = createLatestRequestGate()

export const useCatalogStore = defineStore('catalog', {
  state: () => ({
    products: emptyList(),
    categories: emptyList(),
    brands: emptyList(),
    characteristics: emptyList(),
    providers: emptyList(),
    current: null,
    effectiveAssignments: [],
    directAssignments: [],
    assignmentsLoading: false,
    pageSize: 20,
  }),
  getters: {
    categoryOptions: (state) => state.categories.items.map((item) => ({ id: item.id, label: entityName(item) || item.id })),
    brandOptions: (state) => state.brands.items.map((item) => ({ id: item.id, label: entityName(item) || item.id })),
    characteristicOptions: (state) => state.characteristics.items.map((item) => ({ id: item.id, label: `${item.code} — ${entityName(item)}` })),
  },
  actions: {
    async loadProducts(params = {}) { this.products = await catalogApi.listProducts(params); return this.products },
    async loadCategories(params = {}) { this.categories = await catalogApi.listCategories(params); return this.categories },
    async loadBrands(params = {}) { this.brands = await catalogApi.listBrands(params); return this.brands },
    async loadCharacteristics(params = {}) { this.characteristics = await catalogApi.listCharacteristics(params); return this.characteristics },
    async loadProviders(params = {}) { this.providers = await catalogApi.listProviders(params); return this.providers },
    async loadProduct(id) { this.current = await catalogApi.getProduct(id); return this.current },
    async loadCategory(id) { this.current = await catalogApi.getCategory(id); return this.current },
    async loadBrand(id) { this.current = await catalogApi.getBrand(id); return this.current },
    async loadCharacteristic(id) { this.current = await catalogApi.getCharacteristic(id); return this.current },
    async loadReferenceData() {
      const [categories, brands, providers] = await Promise.all([
        catalogApi.listCategories({ page: 1, limit: 1000 }),
        catalogApi.listBrands({ page: 1, limit: 1000 }),
        catalogApi.listProviders({ page: 1, limit: 1000 }),
      ])
      this.categories = categories
      this.brands = brands
      this.providers = providers
    },
    async loadCategoryCharacteristics(categoryId) {
      const request = characteristicsGate.begin()
      this.assignmentsLoading = true
      try {
        const [effective, direct] = await Promise.all([
          catalogApi.getCategoryCharacteristics(categoryId),
          catalogApi.getCategoryCharacteristics(categoryId, { localOnly: true }),
        ])
        if (!characteristicsGate.isCurrent(request)) return false
        this.effectiveAssignments = effective.items
        this.directAssignments = direct.items
        return true
      } finally {
        if (characteristicsGate.isCurrent(request)) this.assignmentsLoading = false
      }
    },
    clearAssignments() {
      characteristicsGate.begin()
      this.effectiveAssignments = []
      this.directAssignments = []
      this.assignmentsLoading = false
    },
  },
})
